/**
 * Auto-generated from @intelishs-group/schema-core v5.1.0
 * Source: companyTypes — Prospect API
 * Generated: 2026-10-09
 * Do not edit manually. Run: npm run generate:services
 */
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiBaseService } from './api-base.service';
import type {
  BooleanResponseDto,
  CatMainStagesIEnumerableResponseDto,
  ClienteProspectoResponseIEnumerableResponseDto,
  ConvertProspectToClienteRequest,
  ConvertProspectToClienteResponseResponseDto,
  EspecialistProposalsAdvanceRequest,
  MovAgendaDTOIEnumerableResponseDto,
  MovAgendaDTOResponseDto,
  MovAgendaRequest,
  MovAgendaResponseDto,
  MovClienteProspectoDetailResponseResponseDto,
  PreClientsResponseIEnumerableResponseDto,
  ProspecAgregateRequests,
  ProspectCommentsDTOIEnumerableResponseDto,
  ProspectCommentsResponseDto,
  ProspectGenerateResponseResponseDto,
  ProspectResponseDto,
  ProspectStageMovementAdvanceRequest,
  ProspectStageMovementAdvanceResponseResponseDto,
  ProspectStageTimelineDTOIEnumerableResponseDto,
  ProspectUpdateRequest,
  ProspectWithConversionResponseResponseDto,
  ProspectsStatisticsResponseIEnumerableResponseDto,
  ReagendarMovAgendaRequest,
  RegisterProspectCommentsRequest,
  UpdateMovAgendaRequest,
  VEtapaProspectoDTOIEnumerableResponseDto,
} from '@schemas/prospect';

@Injectable({ providedIn: 'root' })
export class ProspectService extends ApiBaseService {
  private baseUrl = `${environment.BaseUrl}/prospect/`;

  /** Obtiene todas las agendas activas. */
  async agenda_All() {
    return this.get<MovAgendaDTOIEnumerableResponseDto>(`${this.baseUrl}Agenda/All`);
  }

  /** Obtiene una agenda por su Id. */
  async agenda_ById(kidagenda: number) {
    return this.get<MovAgendaDTOResponseDto>(`${this.baseUrl}Agenda/ById`, {
      kidagenda: kidagenda.toString(),
    });
  }

  /** Actualiza una agenda existente. */
  async updateAgenda(body: UpdateMovAgendaRequest) {
    return this.put<MovAgendaResponseDto>(`${this.baseUrl}Agenda`, body);
  }

  /** Registra una nueva agenda para un prospecto. */
  async createAgenda(body: MovAgendaRequest) {
    return this.post<MovAgendaResponseDto>(`${this.baseUrl}Agenda`, body);
  }

  /** Elimina (desactiva) una agenda por su Id. */
  async deleteAgenda(kidagenda: number) {
    return this.delete<BooleanResponseDto>(`${this.baseUrl}Agenda`, {
      kidagenda: kidagenda.toString(),
    });
  }

  /** Reagenda: desactiva la agenda anterior y crea una nueva. */
  async agenda_Reagendar(body: ReagendarMovAgendaRequest) {
    return this.post<MovAgendaResponseDto>(`${this.baseUrl}Agenda/Reagendar`, body);
  }

  /** Verifica el estado de salud del servicio. */
  async health() {
    return lastValueFrom(
      this.http.get<unknown>(`${this.baseUrl}Health`),
    );
  }

  /** Verifica el estado de conexión a la base de datos. */
  async health_Database() {
    return lastValueFrom(
      this.http.get<unknown>(`${this.baseUrl}Health/Database`),
    );
  }

  /** Obtiene el catálogo de etapas principales y sub-etapas disponibles para prospectos. */
  async stages() {
    return this.get<CatMainStagesIEnumerableResponseDto>(`${this.baseUrl}Stages`);
  }

  /** Obtiene todos los prospectos activos con su etapa actual. */
  async all() {
    return this.get<VEtapaProspectoDTOIEnumerableResponseDto>(`${this.baseUrl}All`);
  }

  /** Obtiene los prospectos cuyo paso (FkIdPaso) está en el rango [2..20]. */
  async prospects() {
    return this.get<VEtapaProspectoDTOIEnumerableResponseDto>(`${this.baseUrl}Prospects`);
  }

  /** Obtiene los prospectos pendientes de asignación de inplant (FkIdPaso = 21). */
  async prospectsToInplantAssignment() {
    return this.get<VEtapaProspectoDTOIEnumerableResponseDto>(`${this.baseUrl}ProspectsToInplantAssignment`);
  }

  /** Obtiene los prospectos en seguimiento (FkIdPaso ∈ [41, 42, 43]). */
  async prospectsToFollowUp() {
    return this.get<VEtapaProspectoDTOIEnumerableResponseDto>(`${this.baseUrl}ProspectsToFollowUp`);
  }

  /** Obtiene los prospectos para configuración operativa (FkIdPaso = 44). */
  async prospectsForOperationConfig() {
    return this.get<VEtapaProspectoDTOIEnumerableResponseDto>(`${this.baseUrl}ProspectsForOperationConfig`);
  }

  /** Obtiene los prospectos que se encuentran en etapa de Aceptación (pre-clientes). */
  async preClients() {
    return this.get<PreClientsResponseIEnumerableResponseDto>(`${this.baseUrl}PreClients`);
  }

  /** Obtiene los prospectos asignados a especialistas de propuestas. */
  async specialistProposals() {
    return this.get<PreClientsResponseIEnumerableResponseDto>(`${this.baseUrl}SpecialistProposals`);
  }

  /** Obtiene estadísticas agrupadas por etapa principal y sub-etapa. */
  async statistics() {
    return this.get<ProspectsStatisticsResponseIEnumerableResponseDto>(`${this.baseUrl}Statistics`);
  }

  /** Actualiza un prospecto existente. */
  async updateProspect(body: ProspectUpdateRequest) {
    return this.put<ProspectResponseDto>(`${this.baseUrl}Prospect`, body);
  }

  /** Crea un nuevo prospecto en el sistema. */
  async createProspect(body: ProspecAgregateRequests) {
    return this.post<ProspectGenerateResponseResponseDto>(`${this.baseUrl}Prospect`, body);
  }

  /** Avanza un prospecto a la siguiente sub-etapa del pipeline. */
  async stage_Advance(body: ProspectStageMovementAdvanceRequest) {
    return this.post<ProspectStageMovementAdvanceResponseResponseDto>(`${this.baseUrl}Stage/Advance`, body);
  }

  /** Avanza un prospecto a la primera sub-etapa de una etapa principal. */
  async stage_AdvanceToMain(body: ProspectStageMovementAdvanceRequest) {
    return this.post<ProspectStageMovementAdvanceResponseResponseDto>(`${this.baseUrl}Stage/AdvanceToMain`, body);
  }

  /** Avanza un prospecto en el flujo de especialistas de propuestas. */
  async stage_AdvanceSpecialist(body: EspecialistProposalsAdvanceRequest) {
    return this.post<ProspectStageMovementAdvanceResponseResponseDto>(`${this.baseUrl}Stage/AdvanceSpecialist`, body);
  }

  /** Obtiene la información completa de un prospecto por su Id. */
  async byId(idProspect: number) {
    return this.get<ProspectResponseDto>(`${this.baseUrl}ById`, {
      idProspect: idProspect.toString(),
    });
  }

  /** Obtiene la información del prospecto incluyendo, en DatosJson, el ConvertProspectToClienteRequest reconstruido desde MovClienteProspecto. */
  async byId_WithClientConfiguration(idProspect: number) {
    return this.get<ProspectWithConversionResponseResponseDto>(`${this.baseUrl}ById/WithClientConfiguration`, {
      idProspect: idProspect.toString(),
    });
  }

  /** Obtiene los comentarios de un prospecto. */
  async getComments(idProspect: number) {
    return this.get<ProspectCommentsDTOIEnumerableResponseDto>(`${this.baseUrl}Comments`, {
      idProspect: idProspect.toString(),
    });
  }

  /** Registra un nuevo comentario para un prospecto. */
  async createComments(body: RegisterProspectCommentsRequest) {
    return this.post<ProspectCommentsResponseDto>(`${this.baseUrl}Comments`, body);
  }

  /** Obtiene el timeline de etapas de un prospecto. */
  async stageTimeline(idProspect: number) {
    return this.get<ProspectStageTimelineDTOIEnumerableResponseDto>(`${this.baseUrl}StageTimeline/${idProspect}`);
  }

  /** Convierte un prospecto en cliente: envía los datos del prospecto al API de Clientes y registra la relación en prospecto.movclienteprospecto con la información adicional (tipo de servicio, comisión, salario, periodicidad, beneficio, empresas vendedoras). */
  async convertToCliente(body: ConvertProspectToClienteRequest) {
    return this.post<ConvertProspectToClienteResponseResponseDto>(`${this.baseUrl}ConvertToCliente`, body);
  }

  /** Obtiene los clientes prospecto que deben generar una orden periódica hoy según su fecha de inicio y periodicidad. */
  async clienteProspecto_OrdenesPeriodicas() {
    return this.get<ClienteProspectoResponseIEnumerableResponseDto>(`${this.baseUrl}ClienteProspecto/OrdenesPeriodicas`);
  }

  /** Obtiene la relación cliente-prospecto (MovClienteProspecto) activa asociada a un prospecto, incluyendo los datos resumidos del prospecto (RFC, email, nombre, dirección, régimenes fiscales, etc). */
  async clienteProspecto_ByProspectoId(idProspecto: number) {
    return this.get<MovClienteProspectoDetailResponseResponseDto>(`${this.baseUrl}ClienteProspecto/ByProspectoId`, {
      idProspecto: idProspecto.toString(),
    });
  }
}
